import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { can, formatPercent, type DealState } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { marginColor } from '../lib/margin';
import { useAuth } from '../auth/AuthProvider';
import { useMarginFloors } from '../config/ConfigProvider';
import type { View } from '../nav/navModel';
import { AttentionRow, Button, Card, Kpi, Money, Sparkline, StatusBadge, useToast } from '../ui';
import { AttentionStrip } from './AttentionStrip';
import { metricDrill } from './dashboardDrill';
import { freshnessTone, updatesTone, type PriceDutyView } from './priceDutyView';

interface Flagged {
  id: number;
  no: string;
  state: DealState;
  customer_name: string | null;
  margin: number | null;
  warnings: string[];
}
interface CreditFlag {
  customer_id: number;
  customer_name: string;
  limit: number;
  exposure: number;
  overBy: number;
}
type ParamsHealthReason = 'ok' | 'pool_unset' | 'mix_empty' | 'all_eqkg_zero';
interface CostBasis {
  ym: string;
  healthy: boolean;
  reason: ParamsHealthReason;
  conversionPool: number;
  conversionRate: number;
  driverBasis: 'delivered' | 'produced' | 'planned' | 'none';
  driverKg: number;
  productCount: number;
  trailingMonths: number;
}
interface MarginRisk {
  id: number;
  no: string;
  customer_name: string | null;
  erosion: number;
  basisMargin: number | null;
  currentMargin: number | null;
  openValue: number;
  riskScore: number;
}
interface PriceTruth {
  netBiasPct: number;
  driftFlagged: number;
  driftUnpricedSignal: number;
  proof: {
    proven: number;
    stale: number;
    missing: number;
    mismatch: number;
    ok: boolean;
    minShare: number;
  };
}
interface InflationMonth {
  ym: string;
  level: number;
  momPct: number | null;
  reconstructedShare: number;
}
interface InflationIndex {
  months: InflationMonth[];
  latestMomPct: number | null;
  avgMomPct: number | null;
}
interface OverdueTranche {
  id: number;
  no: string;
  customer_name: string | null;
  worstLabel: string;
  daysLate: number;
  count: number;
  remaining: number;
}
interface Dashboard {
  openDeals: number;
  totalOpenRevenue: number;
  totalOpenGross: number;
  openGrossMargin: number | null;
  receivableCarry: number;
  // OWNERNET-GATE (Q-8): the server nulls ownerNet for callers lacking `ownernet.read` (Admin+Finance only);
  // the tile is hidden in that case, so the number is never rendered as null.
  ownerNet: number | null;
  dealsBelowFloor: number;
  priceFreshnessDays: number | null;
  overheadSet: boolean;
  coverage: { actualShare: number; productsWithMissing: number };
  flagged: Flagged[];
  creditFlags: CreditFlag[];
  costBasis: CostBasis;
  marginAtRisk: MarginRisk[];
  priceTruth: PriceTruth;
  inflationIndex: InflationIndex;
  materialInventoryValue: number;
  materialInventoryUnpricedItems: number;
  overdueTranches: OverdueTranche[];
  /** UX-5: an open deal carries an estimated-COGS line ⇒ open gross/margin may be understated. */
  estimatedOpen: boolean;
  /** UX-4: cached reconciliation headline. Chip shows only-on-news (`diverged` — a genuine divergence /
   *  imbalance / orphan, decided server-side at reconciliationHeadlineDiverged, NOT the coarse `!ok` which is
   *  also false on a merely-unsynced book). null = never reconciled = NO DATA = silent. `worstLabel` may be
   *  null even when `diverged` (an orphan, or an imbalance with no single worst tie-out). */
  bookHealth: {
    diverged: boolean;
    worstLabel: string | null;
  } | null;
}

/** Why the month's cost basis is invalid — the specific fix, not just "broken". Empty when healthy. */
const COST_BASIS_REASON_FA: Record<ParamsHealthReason, string> = {
  ok: '',
  pool_unset:
    'استخر تبدیل (دستمزد + سربار) این ماه تعیین نشده — تا آن زمان بهای تمام‌شده فقط مواد است و همهٔ حاشیه‌ها گمراه‌کننده‌اند.',
  mix_empty:
    'ترکیب تولید این ماه خالی است — مخرج جذب وجود ندارد و نرخ تبدیل صفر می‌شود؛ حاشیه‌ها قابل اتکا نیستند.',
  all_eqkg_zero:
    'استخر و ترکیب تعیین شده‌اند اما وزن سیم‌پیچ همهٔ محصولات صفر است — نرخ جذب صفر می‌ماند.',
};
const DRIVER_BASIS_FA: Record<CostBasis['driverBasis'], string> = {
  delivered: 'تحویل‌شده',
  produced: 'تولیدشده',
  planned: 'برنامه‌ریزی‌شده',
  none: '—',
};

interface Brief {
  date: string;
  model: string;
  content: string;
  ts: string;
}

function BriefCard() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user ? can(user.role, 'ai.use') : false;
  const [brief, setBrief] = useState<Brief | null>(null);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api<{ brief: Brief | null; aiConfigured: boolean }>('/api/ai/brief');
    if (r.ok) {
      const b = r.body as { brief: Brief | null; aiConfigured: boolean };
      setBrief(b.brief);
      setAiConfigured(b.aiConfigured);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function generate() {
    setBusy(true);
    const r = await api<Brief>('/api/ai/brief', { method: 'POST' });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    setBrief(r.body as Brief);
    toast('گزارش صبحگاهی تولید شد');
  }

  // NS-3: an EMPTY brief stays quiet in the owner's prime glance — un-drillable prose must never sit on top
  // of the ground-truth numbers. When there is no brief, the owner sees nothing; an admin keeps a slim
  // generate affordance (the brief also auto-generates each morning), so the feature stays reachable.
  if (!brief) {
    if (!isAdmin || !aiConfigured) return null;
    return (
      <div className="row" style={{ margin: '2px 0 14px' }}>
        <Button variant="secondary" onClick={generate} loading={busy}>
          تولید نظر دستیار برای امروز
        </Button>
      </div>
    );
  }

  // A brief exists → show it as the assistant's «نظر دستیار» (a second opinion below the numbers, NORTH_STAR
  // §17), plain advice text; its numbers are deliberately NOT click-wired (the ground truth is the KPI tiles).
  return (
    <Card title={`نظر دستیار — ${brief.date}`}>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 2 }}>{brief.content}</div>
      {isAdmin && aiConfigured ? (
        <div className="row" style={{ marginTop: 10 }}>
          <Button variant="secondary" onClick={generate} loading={busy}>
            تولید دوبارهٔ نظر امروز
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

/**
 * A Control-Tower monitor panel that COLLAPSES when healthy (E13-12): a good day shows a slim one-line
 * green strip (native <details>, expandable to see the numbers), a bad day shows the loud Card so the
 * problem pops. Keeps the always-loud panels (margin-at-risk, worklist, credit, flagged) untouched.
 */
function TowerPanel({
  title,
  ok,
  okSummary,
  loudHeader,
  children,
}: {
  title: string;
  ok: boolean;
  okSummary: string;
  loudHeader?: ReactNode;
  children: ReactNode;
}) {
  if (ok) {
    return (
      <details className="cc-ok-panel">
        <summary className="cc-ok-summary">
          ✓ {title} — <span className="muted">{okSummary}</span>
        </summary>
        <div style={{ marginTop: 10 }}>{children}</div>
      </details>
    );
  }
  return (
    <Card title={title}>
      {loudHeader}
      {children}
    </Card>
  );
}

type Metric = {
  label: string;
  value: ReactNode;
  tone?: 'good' | 'bad' | 'default' | undefined;
  onClick?: (() => void) | undefined;
  title?: string | undefined;
  /** small caveat under the value (UX-5 «شامل اقلام تقریبی») */
  hint?: ReactNode | undefined;
};

export function DashboardScreen({
  onOpenDeal,
  onOpenInquiries,
  onOpenPrices,
  onNavigate,
  onOpenCustomer,
}: {
  onOpenDeal?: (id: number) => void;
  onOpenInquiries?: () => void;
  onOpenPrices?: () => void;
  onNavigate?: ((view: View, focusDealId?: number) => void) | undefined;
  /** open a customer's record (E16-35) — the over-credit-limit list drills here, not a dead-end. */
  onOpenCustomer?: ((customerId: number) => void) | undefined;
}) {
  const floors = useMarginFloors();
  const { user } = useAuth();
  // OWNERNET-GATE (Q-8): «سود خالص مالک» is Admin+Finance only. The server already nulls the value for other
  // roles; hide the tile too so it never renders empty. Mirrors the existing `can(user.role, 'ai.use')` check.
  const canSeeOwnerNet = user ? can(user.role, 'ownernet.read') : false;
  const [d, setD] = useState<Dashboard | null>(null);
  const [duty, setDuty] = useState<PriceDutyView | null>(null);
  const [newInquiries, setNewInquiries] = useState(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (soft = false) => {
    if (soft) setRefreshing(true);
    else setStatus('loading');
    // All reads in parallel; the dashboard drives ready/error, inquiries is best-effort so a hiccup never
    // blocks the owner-net view. The «کارهای امروز» worklist self-fetches inside <AttentionStrip> (the ONE
    // shared /api/attention scan), so it is no longer read here.
    const [dash, inq, dutyR] = await Promise.all([
      api<Dashboard>('/api/dashboard'),
      api<unknown[]>('/api/inquiries?status=new'),
      api<PriceDutyView>('/api/price-duty'),
    ]);
    if (dash.ok) {
      setD(dash.body as Dashboard);
      setDuty(dutyR.ok ? (dutyR.body as PriceDutyView) : null);
      setNewInquiries(inq.ok && Array.isArray(inq.body) ? inq.body.length : 0);
      setStatus('ready');
    } else {
      setStatus('error');
    }
    if (soft) setRefreshing(false);
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  // ---- Error: never leave the screen stuck on a spinner. Offer a retry. ----
  if (status === 'error') {
    return (
      <Card title="داشبورد — نمای مالک">
        <div className="banner banner-err" role="alert">
          بارگذاری داشبورد ناموفق بود. اتصال را بررسی کنید و دوباره تلاش کنید.
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          <Button onClick={() => void load()}>تلاش دوباره</Button>
        </div>
      </Card>
    );
  }

  // ---- Loading: a shaped skeleton, not a bare spinner (perceived speed). ----
  if (status === 'loading' || !d) {
    return (
      <Card title="داشبورد — نمای مالک">
        <div className="kpi-grid" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="kpi kpi-skel">
              <span className="skeleton skeleton-line" style={{ width: '62%' }} />
              <span className="skeleton skeleton-line lg" style={{ width: '44%' }} />
            </div>
          ))}
        </div>
        <span className="visually-hidden">در حال بارگذاری داشبورد…</span>
      </Card>
    );
  }

  const metrics: Metric[] = [
    { label: 'پیشنهاد/قرارداد باز', value: d.openDeals },
    { label: 'درآمد باز', value: <Money value={d.totalOpenRevenue} /> },
    {
      label: 'سود ناخالص باز',
      value: <Money value={d.totalOpenGross} />,
      tone: 'good',
      // UX-5: understated estimated COGS inflates gross — own up to it on the tile itself. Revenue (below)
      // is price-based and untainted, so it carries no footnote.
      hint: d.estimatedOpen ? 'شامل اقلام تقریبی' : undefined,
    },
    {
      label: 'حاشیه ناخالص',
      value: formatPercent(d.openGrossMargin),
      hint: d.estimatedOpen ? 'شامل اقلام تقریبی' : undefined,
    },
    { label: 'هزینهٔ حمل دریافتنی', value: <Money value={d.receivableCarry} /> },
    // OWNERNET-GATE (Q-8): the owner's ownership number — shown to Admin+Finance only. Guarded spread keeps
    // its position; for other roles the server sends null and the tile is omitted entirely.
    ...((canSeeOwnerNet && d.ownerNet !== null
      ? [
          {
            label: 'سود خالص مالک',
            value: <Money value={d.ownerNet} />,
            tone: d.ownerNet < 0 ? 'bad' : 'good',
          },
        ]
      : []) as Metric[]),
    {
      label: 'زیر حد حاشیه',
      value: d.dealsBelowFloor,
      tone: d.dealsBelowFloor > 0 ? 'bad' : 'default',
    },
    {
      // DUTY-1: the freshness number was always here but never went red past the stale window. Tone it
      // bad when the duty authority reports materials older than `staleDays`, so the owner's glance shows
      // "stale", not just a neutral count.
      label: 'تازگی قیمت',
      value: d.priceFreshnessDays == null ? '—' : `${d.priceFreshnessDays} روز`,
      tone: freshnessTone(duty),
      title:
        duty && duty.staleMaterials > 0
          ? `${duty.staleMaterials} ماده قدیمی‌تر از ${duty.staleDays} روز`
          : undefined,
    },
    {
      label: 'سربار و حقوق',
      value: d.overheadSet ? 'تنظیم شده' : 'وارد نشده',
      tone: d.overheadSet ? 'good' : 'bad',
    },
    {
      label: 'پوشش بهای واقعی',
      value: formatPercent(d.coverage.actualShare),
      tone: d.coverage.actualShare >= 0.5 ? 'good' : 'default',
    },
    {
      label: 'ارزش موجودی مواد',
      value: <Money value={d.materialInventoryValue} />,
      title: 'سرمایهٔ در گردش راکد در موجودی مواد اولیه (به میانگین متحرک)',
      // The tile states the PRICED pool. When stock without a confirmed price sits alongside it, the number
      // is an UNDERSTATEMENT — say so on the tile itself, the same way the two «شامل اقلام تقریبی» tiles
      // above own up to their own tilt. No hint while there is nothing to qualify.
      hint: d.materialInventoryUnpricedItems > 0 ? 'بدون موجودی فاقد قیمت قطعی' : undefined,
    },
    {
      label: 'استعلام‌های جدید سایت',
      value: newInquiries,
      tone: newInquiries > 0 ? 'bad' : 'default',
      onClick: onOpenInquiries,
      title: 'باز کردن صندوق استعلام‌ها',
    },
    {
      label: 'فراتر از سقف اعتبار',
      value: d.creditFlags.length,
      tone: d.creditFlags.length > 0 ? 'bad' : 'default',
      title: 'مشتریانی که مانده‌شان از سقف اعتبار گذشته است',
    },
  ];

  // DUTY-1: the accountability half of the price duty — "is the office keeping prices fresh, by whom?"
  // (the number lives in تازگی قیمت; this is whether the human duty is being DONE this week). Owner-glance
  // framing only — the imperative «به‌روزرسانی با شماست» lives on the price-writer's own screen, not here.
  if (duty) {
    metrics.push({
      label: `به‌روزرسانی قیمت این هفته`,
      value: duty.updatesThisWeek,
      tone: updatesTone(duty.updatesThisWeek),
      title: duty.lastUpdate
        ? `آخرین: ${duty.lastUpdate.actor} — ${new Date(duty.lastUpdate.ts).toLocaleDateString('fa-IR')}`
        : 'این هفته کسی قیمت‌ها را به‌روز نکرده',
    });
  }

  // Panel C — price-truth warnings. Net bias ≥ 2% means we pay materially above book ⇒ COGS understated
  // (a display threshold for the headline; the drift authority's own 5% governs `driftFlagged`).
  const pt = d.priceTruth;
  const ptUnproven = pt.proof.stale + pt.proof.missing;
  const ptWarnings: string[] = [];
  if (pt.netBiasPct >= 0.02)
    ptWarnings.push(
      'خرید واقعی به‌طور میانگین بالاتر از کتاب است — بهای تمام‌شده کمتر از واقع برآورد می‌شود؛ قیمت‌ها را به‌روز کنید.',
    );
  if (ptUnproven > 0) ptWarnings.push(`${ptUnproven} گروه مهم پشتوانهٔ تازه ندارند.`);
  if (pt.proof.mismatch > 0)
    ptWarnings.push(`${pt.proof.mismatch} گروه با قیمت کتاب مغایرت دارند.`);
  // Scope of the bias number itself: purchases with an unconfirmed price are not counted as a signal, so
  // the bias above is measured over the PRICED SUBSET only.
  // PREPROD-1 — this used to claim the headline is «کمتر از واقع» (understated). That asserts a DIRECTION
  // the exclusion does not carry: the excluded receipts are unpriced, so their divergence is unknown and
  // could be below book just as easily as above — including them could move the net bias either way. State
  // the scope and leave the sign open, the way PriceDriftCard already does («روی همان بخشِ قیمت‌دار وزن‌دهی شده»).
  if (pt.driftUnpricedSignal > 0)
    ptWarnings.push(
      `خرید ${pt.driftUnpricedSignal} ماده «قیمت تأییدنشده» دارد و در سنجش انحراف شمرده نشده — سوگیری بالا فقط روی بخشِ قیمت‌دار سنجیده شده و ممکن است در هر دو جهت جابه‌جا شود.`,
    );

  // Panel E — material-inflation index. Signed % helper; the sparkline uses only priced (non-zero) months.
  const inf = d.inflationIndex;
  const infLevels = inf.months.map((m) => m.level).filter((l) => l > 0);
  const infLatestRecon = inf.months.at(-1)?.reconstructedShare ?? 0;
  const signedPct = (v: number | null) =>
    v == null ? '—' : `${v >= 0 ? '+' : '−'}${formatPercent(Math.abs(v))}`;

  return (
    <>
      <Card
        title={
          <div className="card-head">
            <span>داشبورد — نمای مالک</span>
            <button
              type="button"
              className="icon-refresh"
              onClick={() => void load(true)}
              disabled={refreshing}
              aria-label="بارگذاری دوباره"
              title="بارگذاری دوباره"
            >
              <span className={refreshing ? 'spin' : ''} aria-hidden="true">
                ↻
              </span>
            </button>
          </div>
        }
      >
        <div className="kpi-grid">
          {metrics.map((m) => {
            // NS-1: a headline number drills to the view that shows its detail (metricDrill = the tested
            // authority). An explicit m.onClick (e.g. inquiries) wins; a non-mapped tile stays a plain div.
            const drill = metricDrill(m.label);
            const onClick =
              m.onClick ?? (drill && onNavigate ? () => onNavigate(drill) : undefined);
            return (
              <Kpi
                key={m.label}
                label={m.label}
                value={m.value}
                tone={m.tone}
                hint={m.hint}
                onClick={onClick}
                title={m.title}
              />
            );
          })}
        </div>
      </Card>

      {/* UX-4: book-health trust chip. Only-on-news — renders solely when the CACHED reconciliation headline
          reports a GENUINE divergence (`diverged`, decided at the authority); silent when green, never-
          reconciled (null = NO DATA), OR merely unsynced (a phantom, never an alarm). Standalone, decoupled
          from the owner-net number; visible to all dashboard.read roles. worstLabel can be null even when
          diverged (orphan/imbalance), so degrade the text gracefully. */}
      {d.bookHealth?.diverged ? (
        <div className="book-health-chip" role="status">
          ⚠ مغایرت دفترها
          {d.bookHealth.worstLabel ? ` — ${d.bookHealth.worstLabel}` : ''}
        </div>
      ) : null}

      {/* NS-3: the AI's «نظر دستیار» sits immediately below the drillable numbers as a second opinion — never
          un-drillable prose ABOVE the ground truth §0 names. Quiet when there is no brief. */}
      <BriefCard />

      {/* Control Tower Panel D — the money-ranked action center «کارهای امروز» (UX-1). The worklist look,
          per-item nav, and accountability actions now live in the ONE reusable <AttentionStrip>, shared with
          the deals + cash landings. On the dashboard it keeps its always-open Card form. */}
      <AttentionStrip
        variant="card"
        onOpenDeal={onOpenDeal}
        onOpenCustomer={onOpenCustomer}
        onNavigate={onNavigate}
      />

      {/* Control Tower Panel A — cost-basis health. When invalid, cost collapses to landed-material and every
          margin above is a lie, so this is loud and sits directly under the KPIs. */}
      <TowerPanel
        title={`سلامت مبنای بهای تمام‌شده — ${d.costBasis.ym}`}
        ok={d.costBasis.healthy}
        okSummary="مبنای جذب معتبر است"
        loudHeader={
          <div className="banner banner-err" role="alert" style={{ marginBottom: 10 }}>
            ⚠ {COST_BASIS_REASON_FA[d.costBasis.reason]} برای اصلاح به «مفروضات ماهانه» بروید.
          </div>
        }
      >
        <div className="fi-warnings" style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          <span>
            استخر تبدیل: <Money value={d.costBasis.conversionPool} />
          </span>
          <span>
            نرخ جذب: <Money value={d.costBasis.conversionRate} /> / کیلوگرم
          </span>
          <span>
            مبنای محرک: {DRIVER_BASIS_FA[d.costBasis.driverBasis]} ({d.costBasis.trailingMonths}{' '}
            ماهه)
          </span>
          <span>
            محرک: {d.costBasis.driverKg.toLocaleString('fa-IR')} کیلوگرم در ماه ·{' '}
            {d.costBasis.productCount} محصول
          </span>
        </div>
      </TowerPanel>

      {/* Control Tower Panel B — margin-at-risk. Open FIXED-price deals bleeding to material inflation,
          worst-first by exposed rials × erosion. One click opens the deal to re-price / renegotiate. */}
      {d.marginAtRisk.length > 0 ? (
        <Card title={`حاشیه در معرض خطر (${d.marginAtRisk.length})`}>
          <ul className="flagged-list">
            {d.marginAtRisk.map((m) => {
              const open = onOpenDeal ? () => onOpenDeal(m.id) : undefined;
              return (
                <AttentionRow key={m.id} onActivate={open}>
                  <div className="fi-top">
                    <span className="fi-no num">{m.no}</span>
                    <span className="warn">بهای مواد +{formatPercent(m.erosion)}</span>
                    <span className="fi-margin num">
                      حاشیه{' '}
                      <span style={{ color: marginColor(m.basisMargin, floors) }}>
                        {formatPercent(m.basisMargin)}
                      </span>{' '}
                      →{' '}
                      <span style={{ color: marginColor(m.currentMargin, floors) }}>
                        {formatPercent(m.currentMargin)}
                      </span>
                    </span>
                  </div>
                  <div className="fi-customer">{m.customer_name || 'مشتری نامشخص'}</div>
                  <div className="fi-warnings">
                    <span>
                      ارزش تحویل‌نشدهٔ در معرض خطر: <Money value={m.openValue} />
                    </span>
                  </div>
                </AttentionRow>
              );
            })}
          </ul>
        </Card>
      ) : null}

      {/* Overdue tranches — portfolio-wide payment risk, worst-days-late first. Was per-deal only; here at a
          glance across all money-carrying deals. One click opens the deal to chase the cheque/collection. */}
      {d.overdueTranches.length > 0 ? (
        <Card title={`اقساط عقب‌افتاده (${d.overdueTranches.length})`}>
          <ul className="flagged-list">
            {d.overdueTranches.map((t) => {
              const open = onOpenDeal ? () => onOpenDeal(t.id) : undefined;
              return (
                <AttentionRow key={t.id} onActivate={open}>
                  <div className="fi-top">
                    <span className="fi-no num">{t.no}</span>
                    <span className="warn">
                      «{t.worstLabel}» {t.daysLate} روز عقب
                      {t.count > 1 ? ` (+${t.count - 1} قسط دیگر)` : ''}
                    </span>
                    <span className="fi-margin num">
                      مانده <Money value={t.remaining} />
                    </span>
                  </div>
                  <div className="fi-customer">{t.customer_name || 'مشتری نامشخص'}</div>
                </AttentionRow>
              );
            })}
          </ul>
        </Card>
      ) : null}

      {/* Control Tower Panel C — price-truth. Is the book itself a lie? Paid-vs-book bias (under-costing) +
          the major-group supplier-proof gate. Under inflation a stale book quietly understates COGS. */}
      <TowerPanel
        title="صحت قیمت‌ها"
        ok={ptWarnings.length === 0}
        okSummary="کتاب قیمت پشتوانه‌دار و هم‌راستا"
        loudHeader={
          <div className="banner banner-err" role="alert" style={{ marginBottom: 10 }}>
            {ptWarnings.map((w, i) => (
              <div key={i}>⚠ {w}</div>
            ))}
          </div>
        }
      >
        <div className="fi-warnings" style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          <span style={{ color: pt.netBiasPct >= 0.02 ? 'var(--danger, #c0392b)' : undefined }}>
            سوگیری خرید به کتاب: {pt.netBiasPct >= 0 ? '+' : '−'}
            {formatPercent(Math.abs(pt.netBiasPct))}
          </span>
          <span>اقلام واگرا: {pt.driftFlagged}</span>
          <span>
            پشتوانهٔ گروه‌های مهم: {pt.proof.proven} معتبر · {pt.proof.stale} کهنه ·{' '}
            {pt.proof.missing} بدون · {pt.proof.mismatch} مغایر
          </span>
        </div>
        {onOpenPrices ? (
          <div style={{ marginTop: 10 }}>
            <Button variant="secondary" onClick={onOpenPrices}>
              باز کردن قیمت‌ها
            </Button>
          </div>
        ) : null}
      </TowerPanel>

      {/* Control Tower Panel E — material-inflation index. How fast is material cost ITSELF rising?
          Cost-share-weighted group book, rolled back month-by-month, so the owner pre-empts the next re-price. */}
      <TowerPanel
        title="شتاب تورم مواد"
        ok={(inf.latestMomPct ?? 0) < 0.02}
        okSummary={`این ماه ${signedPct(inf.latestMomPct)}`}
      >
        <div
          className="fi-warnings"
          style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}
        >
          <span
            style={{
              fontWeight: 600,
              color: (inf.latestMomPct ?? 0) > 0 ? 'var(--danger, #c0392b)' : undefined,
            }}
          >
            این ماه: {signedPct(inf.latestMomPct)}
          </span>
          <span>میانگین شتاب: {signedPct(inf.avgMomPct)}</span>
          {infLevels.length >= 2 ? <Sparkline points={infLevels} /> : null}
        </div>
        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
          شاخص وزنیِ سهم‌بها بر پایهٔ کتاب گروه‌ها
          {infLatestRecon > 0.5 ? ' — ماه‌های قدیمی‌تر بازسازی‌شده (برآوردی)' : ''}.
        </div>
      </TowerPanel>

      {d.priceFreshnessDays == null ? (
        <div className="banner">
          هنوز قیمتی وارد نشده — از تب «قیمت‌ها» شروع کنید تا بهای موتورها محاسبه شود.
        </div>
      ) : null}

      {d.creditFlags.length > 0 ? (
        <Card title={`فراتر از سقف اعتبار (${d.creditFlags.length})`}>
          <ul className="flagged-list">
            {d.creditFlags.map((c) => {
              // Drill into the customer record — where the credit limit and terms are set (E16-35).
              const open = onOpenCustomer ? () => onOpenCustomer(c.customer_id) : undefined;
              return (
                <AttentionRow key={c.customer_id} onActivate={open}>
                  <div className="fi-customer">{c.customer_name}</div>
                  <div className="fi-warnings">
                    <span className="warn">
                      ⚠ مانده <Money value={c.exposure} /> از سقف <Money value={c.limit} /> —{' '}
                      <Money value={c.overBy} /> بیشتر
                    </span>
                  </div>
                </AttentionRow>
              );
            })}
          </ul>
        </Card>
      ) : null}

      <Card title={`نیازمند توجه (${d.flagged.length})`}>
        {d.flagged.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            هیچ پیشنهاد بازی هشدار ندارد. ✓
          </p>
        ) : (
          <ul className="flagged-list">
            {d.flagged.map((f) => {
              const open = onOpenDeal ? () => onOpenDeal(f.id) : undefined;
              return (
                <AttentionRow key={f.id} onActivate={open}>
                  <div className="fi-top">
                    <span className="fi-no num">{f.no}</span>
                    <StatusBadge state={f.state} />
                    <span
                      className="fi-margin num"
                      style={{ color: marginColor(f.margin, floors) }}
                    >
                      {formatPercent(f.margin)}
                    </span>
                  </div>
                  <div className="fi-customer">{f.customer_name || 'مشتری نامشخص'}</div>
                  <div className="fi-warnings">
                    {f.warnings.map((w, i) => (
                      <span key={i} className="warn">
                        ⚠ {w}
                      </span>
                    ))}
                  </div>
                </AttentionRow>
              );
            })}
          </ul>
        )}
      </Card>
    </>
  );
}
